import type { HabitFrequency } from '../interfaces'
import { getDailyKey, getHabitDay, getWeeklyKey } from './dateHelpers'

export type HabitHeatmapState = 'completed' | 'skipped' | 'empty'

export interface HabitHeatmapHabit {
  frequency_type: HabitFrequency
}

export interface HabitHeatmapLog {
  period_key: string
  skipped: boolean
}

export interface HabitHeatmapDay {
  date: Date
  dailyKey: string
  weeklyKey: string
  state: HabitHeatmapState
}

export interface HabitHeatmapWeek {
  weeklyKey: string
  days: HabitHeatmapDay[]
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return next
}

function getWeekStart(date: Date): Date {
  const day = date.getDay() || 7
  return addDays(date, 1 - day)
}

function getLogState(log: HabitHeatmapLog | undefined): HabitHeatmapState {
  if (!log) return 'empty'
  return log.skipped ? 'skipped' : 'completed'
}

export function getHabitHeatmapPeriodKeys(
  referenceDate: Date,
  dayStartTime: string,
  weekCount = 12,
): string[] {
  const weeks = getHabitHeatmapWeeks(
    { frequency_type: 'daily' },
    [],
    referenceDate,
    dayStartTime,
    weekCount,
  )

  return [
    ...new Set([
      ...weeks.flatMap((week) => week.days.map((day) => day.dailyKey)),
      ...weeks.map((week) => week.weeklyKey),
    ]),
  ]
}

export function getHabitHeatmapWeeks(
  habit: HabitHeatmapHabit,
  logs: HabitHeatmapLog[],
  referenceDate: Date,
  dayStartTime: string,
  weekCount = 12,
): HabitHeatmapWeek[] {
  const logByPeriodKey = new Map(logs.map((log) => [log.period_key, log]))
  const endWeekStart = getWeekStart(getHabitDay(referenceDate, dayStartTime))
  const startDate = addDays(endWeekStart, -(Math.max(1, weekCount) - 1) * 7)
  const weeks: HabitHeatmapWeek[] = []

  for (let weekIndex = 0; weekIndex < weekCount; weekIndex++) {
    const weekStart = addDays(startDate, weekIndex * 7)
    const days: HabitHeatmapDay[] = []

    for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
      const date = addDays(weekStart, dayIndex)
      const dailyKey = getDailyKey(date, dayStartTime)
      const weeklyKey = getWeeklyKey(date, dayStartTime)
      const periodKey = habit.frequency_type === 'weekly' ? weeklyKey : dailyKey

      days.push({
        date,
        dailyKey,
        weeklyKey,
        state: getLogState(logByPeriodKey.get(periodKey)),
      })
    }

    weeks.push({
      weeklyKey: getWeeklyKey(weekStart, dayStartTime),
      days,
    })
  }

  return weeks
}
